/** "The trades behind it" (LF-5, screens §2.6): under the rate-position decision, the rate-position block (where the pick leaves the limits, what to do after a flag, what it does) and the swaps the bank will put on, read from the preview's trace. */
import type { PreviewTrace } from "@bsim/domain";
import { decisionIdOf } from "./decisionValues";
import { traceReasonOf } from "../api/riskFields";
import { plainId, settingText } from "./preview/traceText";
import { RatePositionBlock } from "./portfolio/RatePositionBlock";

/** The decision whose position the bank turns into swaps (the engine sizes the notional and takes the tenor from the pick). */
export const TRADES_DECISION = "treasury.ratePosition";

export function TradesBehind({ trace }: { readonly trace: PreviewTrace | null }) {
  const own = (trace?.decisions ?? []).filter((d) => decisionIdOf(d.decisionId) === TRADES_DECISION);
  // The rate position's one setting is the decision itself ("Rate position (target): No hedge to +1.5%"): it is not a trade, so the list shows the engine's reading of what the bank puts on instead.
  const settings = own.flatMap((d) => d.settings.filter((s) => s.label !== d.label));
  // When nothing trades, the engine's own reading says why ("Inside the band", "at the cap"); the standing-policy line stands only without one.
  const reason = own.map((d) => traceReasonOf(d)).find((r) => r !== undefined);
  return (
    <>
      <RatePositionBlock trace={trace} />
      <details className="gx-decide-pol-trades" data-testid="decide.trades">
        <summary>The trades behind it</summary>
        {trace === null ? <p className="gx-small gx-faint" data-testid="decide.trades.working">Working out the trades…</p>
          : settings.length === 0 ? <p className="gx-small gx-muted" data-testid="decide.trades.none">{reason ?? "This position matches your standing policy, so the bank puts on no new swaps this year."}</p>
          : <ul className="gx-small" data-testid="decide.trades.list">{settings.map((s) => <li key={s.label + String(s.to)}>{settingText(s, plainId)}</li>)}</ul>}
      </details>
    </>
  );
}
